import React, { useContext, useState } from "react";
import { useForm } from "react-hook-form";
import { FaCheckCircle, FaExclamationTriangle, FaTimes, FaTrash } from "react-icons/fa";
import { useDispatch, useSelector } from 'react-redux';
import { selectActive } from "../common/slices/location";
import { BlackButton, ButtonBase, LightButton } from "./Buttons";
import FormDatePicker from "./FormDatePicker";
import FormSelectCrewMember from "./FormSelectCrewMember";
import FormSelectCustomer from "./FormSelectCustomer";
import FormSelectScopeOfWork from "./FormSelectScopeOfWork";
import FormTextInput from "./FormTextInput";
import Modal from "./Modal";
import Row from "./Row";
import { H1, P } from "./Typography";
import { createTimeEntry, deleteTimeEntry, modifyTimeEntry } from "../common/slices/timesheet";
import ModalConfirm from "./ModalConfirm";
import FormCheckbox from "./FormCheckbox";
import { ErrorContext } from "../ErrorContext";

const marshall = (input, locationId) => ({
    ...input,
    locationId,
    crewMemberId: input?.crewMember ? { value: input?.crewMember?.id, label: input?.crewMember?.name } : undefined,
    customerId: input?.customer ? { value: input?.customer?.id, label: input?.customer?.name } : undefined,
    scopeId: input?.scope ? { value: input?.scope?.id, label: input?.scope?.name } : undefined
})

const unmarshall = (input) => ({
    ...input,
    crewMemberId: input?.crewMemberId?.value,
    customerId: input?.customerId?.value,
    scopeId: input?.scopeId?.value
})

const ModalEditTimeEntry = ({ timeEntry, state, onClose }) => {
    const errorContext = useContext(ErrorContext);
    const dispatch = useDispatch();
    const locationId = useSelector(selectActive);
    const formValues = marshall(timeEntry, locationId);
    const [showConfirm, setShowConfirm] = useState(false);

    const {
        control,
        formState: { errors, isSubmitting, isValid },
        handleSubmit
    } = useForm({
        values: { ...formValues },
    })

    const formProps = { control, errors, locationId }

    const onSave = async (data) => {
        try {
            const te = unmarshall(data);
            te.id ? await dispatch(modifyTimeEntry(te)) : await dispatch(createTimeEntry(te));
            onClose();
        } catch (ex) {
            errorContext.setError({ errorMessage: "Failed to create / modify timesheet.", forSupport: ex.message })
        }
    }

    const onDelete = async (data) => {
        try {
            setShowConfirm(false);
            await dispatch(deleteTimeEntry(data));
            onClose();
        } catch (ex) {
            errorContext.setError({ errorMessage: "Failed to delete time entry", forSupport: ex.message })
        }
    }

    return (
        <Modal show size="60%" zindex='1200' closeOnEscape onClose={onClose}>
            <Row>
                <H1>{timeEntry?.id ? 'Update' : 'Create'} Time Entry</H1>
                <ButtonBase onClick={onClose} style={{ marginLeft: 'auto' }}><FaTimes size='16px' color="#000" /></ButtonBase>
            </Row>
            {timeEntry?.status === "SYNC_COMPLETE" && <P><FaCheckCircle /> Sync Completed - {timeEntry.syncStatusMessage}</P>}
            {timeEntry?.status === "ERROR" && <P><FaExclamationTriangle /> Sync Failed - {timeEntry.syncStatusMessage}</P>}
            <Row>
                <FormSelectCrewMember {...formProps} name="crewMemberId" label="Crew Member" placeholder="Select a Crew Member" rules={{ required: true }} disabled={timeEntry?.id || timeEntry?.crewMember?.id} />
                <FormDatePicker {...formProps} name="date" label="Date" placeholder="Select time entry date" rules={{ required: true }} disabled={timeEntry.id} minDate={state.startDate.toDate()} maxDate={state.endDate.toDate()} />
            </Row>
            <FormSelectCustomer {...formProps} name="customerId" label="Customer" placeholder="Select a customer" rules={{ required: true }} />
            <Row>
                <FormSelectScopeOfWork {...formProps} name="scopeId" label="Scope of Work" rules={{ required: true }} />
                <FormTextInput {...formProps} name="hours" label="Hours" placeholder="Enter hours" rules={{ required: true, min: 0, max: 24, pattern: /^([0-9]+([.][0-9]*)?|[.][0-9]+)$/i }} />
            </Row>

            <Row>
                <BlackButton onClick={handleSubmit(onSave)} disabled={isSubmitting || !isValid || timeEntry?.status === "SYNC_COMPLETE"}>Save</BlackButton>
                <LightButton onClick={onClose} disabled={isSubmitting}>Cancel</LightButton>
                <FormCheckbox name="isApproved" placeholder="Is time entry approved?" {...formProps} disabled={timeEntry?.status === "SYNC_COMPLETE"} style={{ flex: 1 }} />
                {/* <div style={{ flex: 1 }} /> */}
                {
                    timeEntry?.id &&
                    <LightButton onClick={() => setShowConfirm(true)} disabled={isSubmitting || timeEntry?.status === "SYNC_COMPLETE"}><FaTrash /> Delete</LightButton>
                }
                {
                    timeEntry?.id &&
                    <P>Straight Time: {timeEntry?.adjustedHours?.straightTime} - Overtime: {timeEntry?.adjustedHours?.overtime}</P>
                }
            </Row>
            {showConfirm && <ModalConfirm
                title="Are you sure?"
                description="Are you sure you want to delete this time entry?  This action cannot be undone."
                zindex={1300}
                onConfirm={handleSubmit(onDelete)}
                onClose={() => setShowConfirm(false)}
            />}
        </Modal>
    );
};

export default ModalEditTimeEntry;
